/**
 * DepotManagement — list, edit, and delete depots uploaded via Data Manager.
 *
 * Mounted at /depots. Pulls from GET /api/depots which enriches each row with
 * live bus/stop/driver/conductor counts so the admin sees at-a-glance which
 * depots are populated and which are still empty.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Buildings, MapPin, Phone, Pencil, Trash, Bus, MapPinLine, UserCircle,
  X, CheckCircle, ArrowsClockwise, Plus, Warning,
} from "@phosphor-icons/react";

export default function DepotManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/depots");
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load depots");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (code) => {
    if (!confirm(`Delete depot ${code}? Buses/stops linked to it will remain (only the depot record is removed).`)) return;
    try {
      await api.delete(`/depots/${code}`);
      toast.success(`Depot ${code} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-5" data-testid="admin-depots">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Master Data</div>
          <h1 className="font-display text-xl lg:text-2xl font-bold">Depot Management</h1>
          <p className="text-[12px] lg:text-sm text-muted-foreground mt-1">
            {rows.length} depot{rows.length === 1 ? "" : "s"} loaded. Add or update via Data Manager → Depots.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="depot-refresh"
            className="px-3 py-2 rounded-md text-[12px] font-bold bg-white text-[#1E3A8A] border border-[#1E3A8A] hover:bg-blue-50 inline-flex items-center gap-1.5">
            <ArrowsClockwise size={13} weight="bold"/> Refresh
          </button>
          <a href="/data-manager?tab=depots" data-testid="depot-upload-link"
            className="ktcl-btn-primary px-3 py-2 rounded-md text-[12px] font-bold inline-flex items-center gap-1.5">
            <Plus size={13} weight="bold"/> Add / Upload
          </a>
        </div>
      </div>

      {/* Loading + empty */}
      {loading && (
        <div className="ktcl-card p-12 text-center text-sm text-slate-400">Loading depots…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="ktcl-card p-12 text-center">
          <Buildings size={36} weight="duotone" className="mx-auto text-[#1E3A8A] opacity-60 mb-2"/>
          <h3 className="font-display font-bold text-base">No depots loaded yet</h3>
          <p className="text-[12px] text-slate-500 mt-1">
            Open Data Manager → Depots and upload your KTCL depots sheet, or click <b>Add / Upload</b> above.
          </p>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block ktcl-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr className="border-b border-slate-200">
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600">Code</th>
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600">Name</th>
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600">Manager</th>
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600">District</th>
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600 text-right">Buses</th>
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600 text-right">Stops</th>
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600 text-right">Crew</th>
              <th className="px-4 py-3 font-semibold text-[11px] uppercase tracking-wider text-slate-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.code} data-testid={`depot-row-${d.code}`} className="border-b border-slate-100 hover:bg-blue-50/30">
                <td className="px-4 py-3 font-mono font-bold text-[#1E3A8A]">{d.code}</td>
                <td className="px-4 py-3 font-semibold">{d.name}
                  {d.address && <div className="text-[11px] text-slate-500 font-normal">{d.address}</div>}
                </td>
                <td className="px-4 py-3">
                  <div>{d.manager_name || "—"}</div>
                  {d.manager_mobile && (
                    <a href={`tel:+91${d.manager_mobile}`} className="text-[11px] text-[#1E3A8A] hover:underline">+91 {d.manager_mobile}</a>
                  )}
                </td>
                <td className="px-4 py-3 text-[12px]">{d.district || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold" data-testid={`depot-bus-count-${d.code}`}>{d.bus_count || 0}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold" data-testid={`depot-stop-count-${d.code}`}>{d.bus_stop_count || 0}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold">{(d.driver_count || 0) + (d.conductor_count || 0)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(d)} data-testid={`depot-edit-${d.code}`}
                    className="px-2 py-1 rounded text-[11px] font-bold bg-blue-50 text-[#1E3A8A] hover:bg-blue-100 inline-flex items-center gap-1 mr-1">
                    <Pencil size={11} weight="bold"/> Edit
                  </button>
                  <button onClick={() => remove(d.code)} data-testid={`depot-delete-${d.code}`}
                    className="px-2 py-1 rounded text-[11px] font-bold bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1">
                    <Trash size={11} weight="bold"/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2.5">
        {rows.map((d) => (
          <div key={d.code} data-testid={`depot-card-${d.code}`} className="ktcl-card p-3.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#1E3A8A] text-[12px]">{d.code}</span>
                </div>
                <div className="font-display font-bold text-base truncate">{d.name}</div>
                {d.address && <div className="text-[11px] text-slate-500 truncate">{d.address}</div>}
              </div>
              <button onClick={() => setEditing(d)}
                data-testid={`depot-edit-mobile-${d.code}`}
                className="w-8 h-8 rounded-md bg-blue-50 text-[#1E3A8A] inline-flex items-center justify-center shrink-0"
                aria-label="Edit depot">
                <Pencil size={14} weight="bold"/>
              </button>
            </div>
            {(d.manager_name || d.manager_mobile) && (
              <div className="mt-2 flex items-center gap-3 text-[12px]">
                <span><UserCircle size={12} className="inline mr-1 text-slate-500"/>{d.manager_name || "—"}</span>
                {d.manager_mobile && (
                  <a href={`tel:+91${d.manager_mobile}`} className="text-[#1E3A8A] font-semibold">
                    <Phone size={12} className="inline mr-1"/>+91 {d.manager_mobile}
                  </a>
                )}
              </div>
            )}
            <div className="mt-2.5 grid grid-cols-3 gap-2">
              <Stat label="Buses"  value={d.bus_count}      icon={Bus}/>
              <Stat label="Stops"  value={d.bus_stop_count} icon={MapPinLine}/>
              <Stat label="Crew"   value={(d.driver_count || 0) + (d.conductor_count || 0)} icon={UserCircle}/>
            </div>
          </div>
        ))}
      </div>

      {editing && <DepotEditModal depot={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5 text-center">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold flex items-center justify-center gap-1">
        <Icon size={10} weight="fill" className="text-[#1E3A8A]"/> {label}
      </div>
      <div className="font-display font-extrabold text-base tabular-nums">{value || 0}</div>
    </div>
  );
}

function DepotEditModal({ depot, onClose, onSaved }) {
  const [f, setF] = useState({
    code: depot.code || "",
    name: depot.name || "",
    address: depot.address || "",
    manager_name: depot.manager_name || "",
    manager_mobile: depot.manager_mobile || "",
    lat: depot.lat ?? "",
    lng: depot.lng ?? "",
    district: depot.district || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      // Only include `code` in payload if it actually changed — this keeps
      // the backend rename path off unless the operator explicitly edited it.
      const payload = { ...f,
        lat: f.lat === "" ? undefined : parseFloat(f.lat),
        lng: f.lng === "" ? undefined : parseFloat(f.lng),
      };
      if (payload.code === depot.code) delete payload.code;
      await api.patch(`/depots/${depot.code}`, payload);
      toast.success(payload.code && payload.code !== depot.code
        ? `Renamed ${depot.code} → ${payload.code}`
        : `${depot.code} updated`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4"
      data-testid="depot-edit-dialog" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap text-slate-500">Depot</div>
            <h2 className="font-display font-bold text-lg">{depot.code} · Edit</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md bg-slate-100 text-slate-500 inline-flex items-center justify-center" aria-label="Close">
            <X size={16}/>
          </button>
        </div>

        {/* Depot code — editable with collision-safe rename on save */}
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Depot Code *</span>
          <input value={f.code ?? ""}
            onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
            data-testid="depot-edit-field-code"
            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono uppercase font-bold tracking-wider outline-none focus:border-[#1E3A8A]"/>
          {f.code !== depot.code && (
            <div className="text-[10.5px] text-amber-700 mt-1 flex items-start gap-1">
              <Warning size={11} weight="fill" className="mt-0.5 shrink-0"/>
              <span>Renaming the code is allowed. Buses / stops linked by depot NAME stay intact.</span>
            </div>
          )}
        </label>

        {[
          { k: "name",            l: "Depot Name *" },
          { k: "address",         l: "Address" },
          { k: "manager_name",    l: "Manager Name" },
          { k: "manager_mobile",  l: "Manager Mobile" },
          { k: "lat",             l: "Latitude" },
          { k: "lng",             l: "Longitude" },
          { k: "district",        l: "District" },
        ].map((field) => (
          <label key={field.k} className="block">
            <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">{field.l}</span>
            <input value={f[field.k] ?? ""}
              onChange={(e) => setF({ ...f, [field.k]: e.target.value })}
              data-testid={`depot-edit-field-${field.k}`}
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
          </label>
        ))}

        <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose}
            className="flex-1 px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={save} disabled={busy || !f.code?.trim() || !f.name?.trim()}
            data-testid="depot-edit-save"
            className="ktcl-btn-primary flex-1 px-3 py-2 rounded-md text-sm font-bold disabled:opacity-50">
            {busy ? "Saving…" : <><CheckCircle size={13} className="inline mr-1" weight="bold"/> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}
